import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Info, CheckCircle, AlertTriangle, AlertCircle, CheckCheck, X, Loader2, ChevronRight,
} from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { PageHeader } from '@/components/common/PageHeader';
import type { NotificationItem } from '@/services/notificationService';

const TYPE_STYLE: Record<NotificationItem['type'], { icon: any; tone: string }> = {
  info:    { icon: Info,          tone: 'text-primary bg-primary/10' },
  success: { icon: CheckCircle,   tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20' },
  warning: { icon: AlertTriangle, tone: 'text-amber-600 bg-amber-50 dark:bg-amber-950/20' },
  error:   { icon: AlertCircle,   tone: 'text-red-500 bg-red-50 dark:bg-red-950/20' },
};

type Tab = 'all' | 'unread';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { items, unreadCount, loading, markRead, markAllRead, dismiss } = useNotifications();
  const [tab, setTab] = useState<Tab>('all');

  const openItem = async (n: NotificationItem) => {
    if (!n.isRead) await markRead(n.id);
    if (n.href) navigate(n.href);
  };

  const onDismiss = async (e: React.MouseEvent, n: NotificationItem) => {
    e.stopPropagation();
    await dismiss(n.id);
  };

  const timeAgo = (iso: string) => {
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const visible = tab === 'unread' ? items.filter((n) => !n.isRead) : items;

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Notifications"
        description={unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
        icon={Bell}
        actions={
          unreadCount > 0 ? (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <CheckCheck className="h-3.5 w-3.5" />Mark all read
            </button>
          ) : undefined
        }
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 w-fit">
        {([
          { id: 'all', label: 'All', count: items.length },
          { id: 'unread', label: 'Unread', count: unreadCount },
        ] as { id: Tab; label: string; count: number }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                tab === t.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground border border-dashed border-border rounded-xl">
          <CheckCheck className="h-8 w-8 opacity-30" />
          <p className="text-sm">{tab === 'unread' ? 'No unread notifications' : "You're all caught up"}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((n) => {
            const { icon: Icon, tone } = TYPE_STYLE[n.type] || TYPE_STYLE.info;
            return (
              <div
                key={n.id}
                onClick={() => openItem(n)}
                className={`group flex items-start gap-3 p-4 rounded-xl border bg-card hover:border-primary/20 hover:shadow-sm transition-all ${
                  n.href ? 'cursor-pointer' : ''
                } ${n.isRead ? 'border-border opacity-70' : 'border-primary/20'}`}
              >
                <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${tone}`}>
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {!n.isRead && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                    <p className="text-sm font-medium">{n.title}</p>
                  </div>
                  {n.description && <p className="text-xs text-muted-foreground mt-0.5">{n.description}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.createdAt)}</p>
                </div>
                {n.href && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />}
                <button
                  onClick={(e) => onDismiss(e, n)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0 mt-0.5"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
